import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api, { patientAPI, doctorAPI, nurseAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ArrowLeft, AlertTriangle, Printer, RefreshCw } from 'lucide-react';
import ForceCloseModal from '../../components/ForceCloseModal';
import { toast } from 'sonner';
import PrintableSummary from '../../components/Print/PrintableSummary';
import { PatientOverviewTab } from './detail/PatientOverviewTab';
import { PatientTimelineTab } from './detail/PatientTimelineTab';
import { PatientActionsTab } from './detail/PatientActionsTab';
import { EditPatientDialog } from './detail/EditPatientDialog';
import { logger } from '../../lib/logger';

const EMPTY_PRINT_DATA = { prescriptions: [], labTests: [], imagingTests: [], procedures: [], admissions: [] };

const usePatientPermissions = () => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole(['super_admin', 'admin']);
  const isReceptionist = hasRole('receptionist');
  const isDoctor = hasRole('doctor');
  const isNurse = hasRole('nurse');
  return {
    isReceptionist,
    canDelete: isAdmin || isReceptionist,
    canAssign: isAdmin || isReceptionist,
    canPrescribe: isDoctor,
    canOrderTests: isDoctor || isNurse,
    canAddNotes: isDoctor || isNurse,
    canRefer: isDoctor || isNurse || isAdmin,
    canEditPatient: isAdmin || isReceptionist,
    canForceClose: isAdmin || isReceptionist,
    canCloseAndDischarge: isAdmin || isReceptionist,
  };
};

const PatientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const permissions = usePatientPermissions();

  const [patient, setPatient] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [nurses, setNurses] = useState([]);
  const [printData, setPrintData] = useState(EMPTY_PRINT_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [forceCloseOpen, setForceCloseOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(new URLSearchParams(location.search).get('tab') || 'overview');

  const fetchPrintData = useCallback(async () => {
    try {
      const params = { patient_id: id };
      const [prescRes, labRes, imgRes, procRes, admRes] = await Promise.all([
        api.get('/prescriptions', { params }),
        api.get('/lab-tests', { params }),
        api.get('/imaging-tests', { params }),
        api.get('/procedures', { params }),
        api.get('/admissions', { params }),
      ]);
      setPrintData({
        prescriptions: prescRes.data.prescriptions || prescRes.data || [],
        labTests: labRes.data.lab_tests || labRes.data || [],
        imagingTests: imgRes.data.imaging_tests || imgRes.data || [],
        procedures: procRes.data.procedures || procRes.data || [],
        admissions: admRes.data.admissions || admRes.data || [],
      });
    } catch (err) {
      logger.error('Error loading print data (non-critical):', err);
    }
  }, [id]);

  const fetchData = useCallback(async () => {
    try {
      const [patientRes, timelineRes, doctorsRes, nursesRes] = await Promise.all([
        patientAPI.getById(id),
        patientAPI.getTimeline(id),
        doctorAPI.getAll(),
        nurseAPI.getAll(),
      ]);
      setPatient(patientRes.data);
      setTimeline(timelineRes.data.timeline || timelineRes.data || []);
      setDoctors(doctorsRes.data.doctors || doctorsRes.data || []);
      setNurses(nursesRes.data.nurses || nursesRes.data || []);
      setError(null);
      fetchPrintData();
    } catch (err) {
      logger.error('Error loading patient details:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to load patient details';
      setError(errorMessage);
      toast.error(errorMessage);
      setPatient(null);
    } finally {
      setLoading(false);
    }
  }, [id, fetchPrintData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="patient-detail-loading">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mb-4"></div>
          <p className="text-slate-600">Loading patient details...</p>
        </div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="patient-detail-error">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">{error || 'Patient Not Found'}</h2>
            <p className="text-slate-600 mb-6">
              {error === 'Patient not found or not assigned to you'
                ? 'This patient is not assigned to you or does not exist in your institution.'
                : 'Unable to load patient details. This might be a connection issue or the patient may not exist.'}
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/patients')}>
                <ArrowLeft className="w-4 h-4 mr-2" />Back to Patients
              </Button>
              <Button onClick={() => { setLoading(true); setError(null); fetchData(); }} className="bg-red-500 hover:bg-red-600">
                <RefreshCw className="w-4 h-4 mr-2" />Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="printable-section" data-testid="patient-detail-page">
      <div className="hidden print:block print-header">
        <h1>HMC Buddy - Patient Profile</h1>
        <p>Healventure Hospital</p>
      </div>

      <header className="page-header glass-header no-print">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/patients')} className="rounded-full" data-testid="back-to-patients-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">{patient.full_name}</h1>
              <p className="text-slate-500">Patient ID: {patient.patient_id}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => window.print()} className="no-print" data-testid="print-patient-btn">
              <Printer className="w-4 h-4 mr-2" />Print
            </Button>
            <Badge className={patient.patient_type === 'indoor' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>
              {patient.patient_type === 'indoor' ? 'Indoor' : 'Outdoor'}
            </Badge>
            <Badge className={patient.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-800'}>
              {patient.status || 'active'}
            </Badge>
          </div>
        </div>
      </header>

      <div className="page-content">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
            <TabsTrigger value="actions" data-testid="tab-actions">Actions</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <PatientOverviewTab patient={patient} doctors={doctors} nurses={nurses} canEdit={permissions.canEditPatient} onEdit={() => setEditDialogOpen(true)} />
          </TabsContent>
          <TabsContent value="timeline">
            <PatientTimelineTab timeline={timeline} />
          </TabsContent>
          <TabsContent value="actions">
            <PatientActionsTab patient={patient} doctors={doctors} nurses={nurses} permissions={permissions} onRefresh={fetchData} onForceClose={() => setForceCloseOpen(true)} />
          </TabsContent>
        </Tabs>
      </div>

      <EditPatientDialog open={editDialogOpen} onOpenChange={setEditDialogOpen} patient={patient} onSaved={fetchData} />

      <PrintableSummary patient={patient} {...printData} />

      <ForceCloseModal
        open={forceCloseOpen}
        onOpenChange={setForceCloseOpen}
        patientId={id}
        patientName={patient?.full_name}
        userRole={user?.role}
        onSuccess={() => { fetchData(); navigate('/patients'); }}
      />
    </div>
  );
};

export default PatientDetail;
